import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { RegsIndex, Waters, regsStateName } from '../../domain/regs-models';

@Component({
  selector: 'app-keep-it-waters-picker',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="picker-page">
      <div class="picker-header">
        <button type="button" class="icon-circle-btn" (click)="back.emit()" aria-label="Back">
          <span class="material-icons">chevron_left</span>
        </button>
        <div class="picker-header-text">
          <h2 class="picker-title">Change state</h2>
          <p class="picker-sub">Showing {{ currentWaters.label }}</p>
        </div>
        <button type="button" class="icon-circle-btn" (click)="close.emit()" aria-label="Close">
          <span class="material-icons">close</span>
        </button>
      </div>

      <div class="picker-content">
        @if (states.length > 0) {
          <div class="section-label">State waters</div>
          <div class="waters-list">
            @for (w of states; track w.key) {
              <button
                type="button"
                class="water-row"
                [class.water-row--active]="w.key === currentWaters.key"
                (click)="selectWaters(w)"
              >
                <div class="water-icon">
                  <span class="material-icons">place</span>
                </div>
                <div class="water-text">
                  <span class="water-title">{{ title(w) }}</span>
                  <span class="water-desc">
                    {{ w.label }}{{ isHere(w) ? ' · You’re here' : '' }}
                  </span>
                </div>
                @if (w.key === currentWaters.key) {
                  <span class="showing-pill">Showing</span>
                } @else {
                  <span class="material-icons chevron">chevron_right</span>
                }
              </button>
            }
          </div>
        }

        @if (federal.length > 0) {
          <div class="section-label" style="margin-top: 18px;">Federal waters</div>
          <div class="waters-list">
            @for (w of federal; track w.key) {
              <button
                type="button"
                class="water-row"
                [class.water-row--active]="w.key === currentWaters.key"
                (click)="selectWaters(w)"
              >
                <div class="water-icon water-icon--federal">
                  <span class="material-icons">waves</span>
                </div>
                <div class="water-text">
                  <span class="water-title">{{ title(w) }}</span>
                  <span class="water-desc">
                    3–200 NM offshore{{ isHere(w) ? ' · You’re here' : '' }}
                  </span>
                </div>
                @if (w.key === currentWaters.key) {
                  <span class="showing-pill">Showing</span>
                } @else {
                  <span class="material-icons chevron">chevron_right</span>
                }
              </button>
            }
          </div>
        }
      </div>
    </div>
  `,
  styles: [
    `
      .picker-page {
        display: flex;
        flex-direction: column;
        height: 100%;
        background: #ffffff;
      }
      .picker-header {
        display: flex;
        align-items: center;
        padding: 12px 16px;
        border-bottom: 1px solid #ededf0;
      }
      .picker-header-text {
        flex: 1;
        margin: 0 10px;
      }
      .picker-title {
        font-size: 17px;
        font-weight: 600;
        color: #1c1c1e;
        margin: 0;
      }
      .picker-sub {
        font-size: 12.5px;
        color: #8a8a8e;
        margin: 2px 0 0;
      }
      .icon-circle-btn {
        width: 34px;
        height: 34px;
        border-radius: 50%;
        border: none;
        background: #f2f2f6;
        color: #3a3a3c;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
      }
      .icon-circle-btn .material-icons {
        font-size: 20px;
      }
      .picker-content {
        flex: 1;
        overflow-y: auto;
        padding: 16px;
      }
      .section-label {
        font-size: 12px;
        font-weight: 600;
        color: #8a8a8e;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        margin-bottom: 8px;
        padding-left: 4px;
      }
      .waters-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .water-row {
        display: flex;
        align-items: center;
        padding: 11px 14px;
        background: #ffffff;
        border: 1px solid #e5e5ea;
        border-radius: 16px;
        cursor: pointer;
        text-align: left;
        transition: background 120ms ease;
      }
      .water-row:hover {
        background: #f9f9fb;
      }
      .water-icon {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        background: #eaf2fe;
        color: #2463eb;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: none;
        margin-right: 12px;
      }
      .water-icon--federal {
        background: #e3f5ea;
        color: #137a43;
      }
      .water-icon .material-icons {
        font-size: 22px;
      }
      .water-text {
        flex: 1;
        display: flex;
        flex-direction: column;
      }
      .water-title {
        font-size: 15.5px;
        font-weight: 600;
        color: #1c1c1e;
      }
      .water-desc {
        font-size: 13px;
        color: #8a8a8e;
        margin-top: 2px;
      }
      .showing-pill {
        padding: 4px 10px;
        background: #e3f5ea;
        color: #137a43;
        font-size: 13px;
        font-weight: 600;
        border-radius: 999px;
      }
      .chevron {
        color: #b4b4b8;
        font-size: 20px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KeepItWatersPickerComponent {
  @Input({ required: true }) idx!: RegsIndex;
  @Input({ required: true }) currentWaters!: Waters;
  @Input() hereWaters?: Waters | null;

  @Output() select = new EventEmitter<Waters>();
  @Output() back = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  get states(): Waters[] {
    return this.idx ? this.idx.allWaters.filter((w) => !w.isFederal) : [];
  }

  get federal(): Waters[] {
    return this.idx ? this.idx.allWaters.filter((w) => w.isFederal) : [];
  }

  title(w: Waters): string {
    if (w.isFederal) return w.label;
    if (w.key === 'fl') return 'Florida (Atlantic)';
    if (w.key === 'fl-gulf') return 'Florida (Gulf)';
    return regsStateName(w.key);
  }

  isHere(w: Waters): boolean {
    return this.hereWaters != null && this.hereWaters.key === w.key;
  }

  selectWaters(w: Waters): void {
    this.select.emit(w);
  }
}
